/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { CHECK_ROUTINE_RESULT_TYPE, PHASE } from './constants';
import { readData } from './readData';
import { execute } from './execute';
import { executeCheck } from './execute_check';
import { pollingStop } from './pollingStop';
import { monitoring } from './monitoring';
import { stop } from './stop';
import { exit } from './exit';
import { cooling, igOffStandby } from './cooling';
import { dispose } from './dispose';

export default {
  name: "requirement158",
  data() {
    return {
      phase: PHASE.STANDBY_ENGINE_START,
      isLoading: false,
      coolingTime: null,
      timerInfo: "",
      modal_requirement_158: false,
      isCancelled: false,
      gpfErrorTxt: '',
      isDispose: true
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true },
  },

  computed: {
    ...mapState({
      netAppId: (state) => state.netAppConnector.pairingAppId,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      const colorMap = {
        // danger
        [PHASE.START_CONDITION_ERROR]: "danger",
        [PHASE.GPF_ERROR]: "danger",
        [PHASE.FIRST_COOLING_COMPLETED]: "danger",
        [PHASE.SECOND_COOLING_COMPLETED]: "danger",
        [PHASE.THIRD_COOLING_COMPLETED]: "danger",
        [PHASE.COMMUNICATION_ERROR]: "danger",
        [PHASE.COMMON_ERROR]: "danger",

        // success
        [PHASE.SUCCESS]: "success",
        [PHASE.COOLING_COMPLETED]: "success",

        // secondary
        [PHASE.STANDBY_ENGINE_START]: "secondary",
        [PHASE.STANDBY_FORCED_PLAYBACK]: "secondary",
        [PHASE.STANDBY_PLAYBACK]: "secondary",
        [PHASE.EXECUTION]: "secondary",
        [PHASE.EXECUTION_MONITORING_STATUS]: "secondary",
        [PHASE.EXECUTION_MONITORING_CANCEL]: "secondary",
        [PHASE.COOLING_DISPLAY]: "secondary",
        [PHASE.INTERRUPTION_COOLING_DISPLAY]: "secondary",
        [PHASE.FIRST_COOLING_DISPLAY]: "secondary",
        [PHASE.SECOND_COOLING_DISPLAY]: "secondary",
        [PHASE.THIRD_COOLING_DISPLAY]: "secondary",
        [PHASE.INSTRUCTION_DISPLAY]: "secondary",
        [PHASE.IG_OFF_STANDBY]: "secondary",
      };

      return colorMap[this.phase];
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 確認
      const confirmationTitle = 'message.common_title_confirmation';
      // 実行中
      const executionTitle = 'individual.common_title_execution';
      // 成功
      const successTitle = 'message.common_title_success';
      // エラー
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.STANDBY_ENGINE_START]: confirmationTitle,
        [PHASE.STANDBY_FORCED_PLAYBACK]: confirmationTitle,
        [PHASE.STANDBY_PLAYBACK]: confirmationTitle,
        [PHASE.EXECUTION]: confirmationTitle,
        [PHASE.EXECUTION_MONITORING_STATUS]: executionTitle,
        [PHASE.EXECUTION_MONITORING_CANCEL]: confirmationTitle,
        [PHASE.COOLING_DISPLAY]: executionTitle,
        [PHASE.INSTRUCTION_DISPLAY]: confirmationTitle,
        [PHASE.IG_OFF_STANDBY]: executionTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.COOLING_COMPLETED]: successTitle,
        [PHASE.INTERRUPTION_COOLING_DISPLAY]: executionTitle,
        [PHASE.START_CONDITION_ERROR]: errorTitle,
        [PHASE.GPF_ERROR]: errorTitle,
        [PHASE.FIRST_COOLING_DISPLAY]: executionTitle,
        [PHASE.FIRST_COOLING_COMPLETED]: errorTitle,
        [PHASE.SECOND_COOLING_DISPLAY]: executionTitle,
        [PHASE.SECOND_COOLING_COMPLETED]: errorTitle,
        [PHASE.THIRD_COOLING_DISPLAY]: executionTitle,
        [PHASE.THIRD_COOLING_COMPLETED]: errorTitle,
        [PHASE.COMMUNICATION_ERROR]: errorTitle,
        [PHASE.COMMON_ERROR]: errorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * ダイアログの表示文言
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.STANDBY_ENGINE_START]: `${this.i18nWsKey}.REQ_WS_158_TXT_1`,
        [PHASE.STANDBY_FORCED_PLAYBACK]: `${this.i18nWsKey}.REQ_WS_158_TXT_2`,
        [PHASE.STANDBY_PLAYBACK]: `${this.i18nWsKey}.REQ_WS_158_TXT_3`,
        [PHASE.EXECUTION]: `${this.i18nWsKey}.REQ_WS_158_TXT_4`,
        [PHASE.EXECUTION_MONITORING_STATUS]: `${this.i18nWsKey}.REQ_WS_158_TXT_5`,
        [PHASE.EXECUTION_MONITORING_CANCEL]: `${this.i18nWsKey}.REQ_WS_158_TXT_6`,
        [PHASE.COOLING_DISPLAY]: this.timerInfo,
        [PHASE.INSTRUCTION_DISPLAY]: `${this.i18nWsKey}.REQ_WS_158_TXT_8`,
        [PHASE.IG_OFF_STANDBY]: this.timerInfo,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_158_TXT_10`,
        [PHASE.INTERRUPTION_COOLING_DISPLAY]: this.timerInfo,
        [PHASE.COOLING_COMPLETED]: `${this.i18nWsKey}.REQ_WS_158_TXT_12`,
        [PHASE.START_CONDITION_ERROR]: `${this.i18nWsKey}.REQ_WS_158_TXT_13`,
        [PHASE.GPF_ERROR]: this.gpfErrorTxt,
        [PHASE.FIRST_COOLING_DISPLAY]: this.timerInfo,
        [PHASE.FIRST_COOLING_COMPLETED]: `${this.i18nWsKey}.REQ_WS_158_TXT_16`,
        [PHASE.SECOND_COOLING_DISPLAY]: this.timerInfo,
        [PHASE.SECOND_COOLING_COMPLETED]: `${this.i18nWsKey}.REQ_WS_158_TXT_18`,
        [PHASE.THIRD_COOLING_DISPLAY]: this.timerInfo,
        [PHASE.THIRD_COOLING_COMPLETED]: `${this.i18nWsKey}.REQ_WS_158_TXT_20`,
        [PHASE.COMMUNICATION_ERROR]: `${this.i18nWsKey}.REQ_WS_158_TXT_21`,
        [PHASE.COMMON_ERROR]: `individual.error_communication_error`,
      };
      return this.$te(messageMap[this.phase]) ? this.$t(messageMap[this.phase]) : messageMap[this.phase];
    },

    /**
     * ダイアログのキャンセルボタンの表示有無
     * @returns {boolean} 表示する場合はtrueを返す
     */
    showButtonCancel() {
      return this.phase === PHASE.EXECUTION;
    },

    /**
     * ダイアログのOKボタンの表示有無
     * @returns {boolean} 表示する場合はtrueを返す
     */
    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.STANDBY_ENGINE_START]: true,
        [PHASE.EXECUTION]: true,
        [PHASE.INSTRUCTION_DISPLAY]: true,
        [PHASE.SUCCESS]: true,
        [PHASE.COOLING_COMPLETED]: true,
        [PHASE.FIRST_COOLING_COMPLETED]: true,
        [PHASE.SECOND_COOLING_COMPLETED]: true,
        [PHASE.THIRD_COOLING_COMPLETED]: true,
        [PHASE.START_CONDITION_ERROR]: true,
        [PHASE.GPF_ERROR]: true,
        [PHASE.COMMUNICATION_ERROR]: true,
        [PHASE.COMMON_ERROR]: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * ダイアログのYesボタンの表示有無
     * @returns {boolean} 表示する場合はtrueを返す
     */
    showButtonYes() {
      const showYesButtonMap = {
        [PHASE.STANDBY_FORCED_PLAYBACK]: true,
        [PHASE.STANDBY_PLAYBACK]: true,
        [PHASE.EXECUTION_MONITORING_CANCEL]: true
      };
      let result = showYesButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * ダイアログのNoボタンの表示有無
     * @returns {boolean} 表示する場合はtrueを返す
     */
    showButtonNo() {
      const showNoButtonMap = {
        [PHASE.STANDBY_FORCED_PLAYBACK]: true,
        [PHASE.STANDBY_PLAYBACK]: true,
        [PHASE.EXECUTION_MONITORING_CANCEL]: true
      };
      let result = showNoButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_158 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_158 = false;
    },

    //----- PHASE操作メソッド -----------------------------------------------//

    /** 実行ダイアログを表示 */
    showExecution() {
      this.phase = PHASE.EXECUTION;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /** データ読出し処理 */
    async readDataProcess() {
      // データ読出しAPI実行
      await readData(this);
    },

    /** 実行処理 */
    async executeProcess() {
      // 実行API実行
      await execute(this);
    },

    /**結果確認処理 */
    async executeCheck() {
      await executeCheck(this);
    },

    /** 中断処理 */
    async pollingStopProcess() {
      // 中断API実行
      await pollingStop(this);
      this.phase = PHASE.EXECUTION_MONITORING_CANCEL;
    },

    /** 停止処理 */
    async stopProcess() {
      // 停止API実行
      await stop(this, CHECK_ROUTINE_RESULT_TYPE.STOP);
    },

    /**
     * 停止監視処理(通常/中断)
     */
    async monitoringProcess() {
      // 停止監視API実行
      await monitoring(this);
    },

    /** 終了処理 */
    async exitProcess() {
      if (this.isDispose) {
        // disposeAPI実行
        await dispose(this);
      } else {
        // 終了API実行
        await exit(this);
      }
    },

    /**
     * 冷却中処理
     * @param {string} checkRoutineData ルーチン結果確認の返り値
     */
    async coolingProcess(checkRoutineData) {
      await cooling(this, checkRoutineData);
    },

    /**
     * キャンセルボタン押下後の処理
     */
    onCancelClicked() {
      this.closeWizard();
    },

    /**
     * OKボタン押下後の処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.STANDBY_ENGINE_START:
          await this.readDataProcess();
          break;
        case PHASE.EXECUTION:
          await this.executeProcess();
          break;
        case PHASE.INSTRUCTION_DISPLAY:
          await igOffStandby(this);
          break;
        case PHASE.SUCCESS:
        case PHASE.COOLING_COMPLETED:
        case PHASE.GPF_ERROR:
        case PHASE.FIRST_COOLING_COMPLETED:
        case PHASE.SECOND_COOLING_COMPLETED:
        case PHASE.THIRD_COOLING_COMPLETED:
        case PHASE.START_CONDITION_ERROR:
        case PHASE.COMMON_ERROR:
          this.closeWizard();
          break;
        case PHASE.COMMUNICATION_ERROR:
          await this.monitoringProcess();
          break;
        default:
          break;
      }
    },

    /**
     * Yesボタン押下後の処理
     */
    async onYesClicked() {
      switch (this.phase) {
        case PHASE.STANDBY_FORCED_PLAYBACK:
        case PHASE.STANDBY_PLAYBACK:
          this.showExecution();
          break;
        case PHASE.EXECUTION_MONITORING_CANCEL:
          await this.stopProcess();
          break;
        default:
          break;
      }
    },

    /**
     * Noボタン押下後の処理
     */
    async onNoClicked() {
      switch (this.phase) {
        case PHASE.STANDBY_FORCED_PLAYBACK:
        case PHASE.STANDBY_PLAYBACK:
          this.closeWizard();
          break;
        case PHASE.EXECUTION_MONITORING_CANCEL:
          this.isCancelled = false;
          this.phase = PHASE.EXECUTION_MONITORING_STATUS;
          await this.executeCheck();
          break;
        default:
          break;
      }
    },

    /** 
     * 停止ボタン押下後の処理
     */
    async onStopClicked() {
      this.isCancelled = true;
      await this.pollingStopProcess();
    },

    //----- 異常コールバックメソッド -----------------------------------------------//

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    async validateErrorResult(result) {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        async (result) => { result; await this.showUnexpectedError(); },
      );
    },

    /** 共通エラー */
    async showUnexpectedError() {
      // 終了処理
      await this.exitProcess();
      this.phase = PHASE.COMMON_ERROR;
    },
  },

  mounted() {
    this.init();
  },
};